import { useEffect, useReducer, useRef, useState } from 'react';
import './createOrganization.css';
import { Navigate, useNavigate } from 'react-router-dom';
import { DASHBOARD_PATH } from '../../router/RouteConstants';
import { useIntl } from 'react-intl';
import CreateOrganizationFormReducer from './reducer/CreateOrganizationFormReducer';
import AIEduInputText from '../../components/common/Input/AIEduInputText';
import AIEduInputNumber from '../../components/common/Input/AIEduInputNumber';
import AIEduButton from '../../components/common/Button/AIEduButton';
import Header from '../../components/layout/Header/Header';
import AIEduDialog from '../../components/common/Dialog/AIEduDialog';
import AIEduDropDown from '../../components/common/DropDown/AIEduDropDown';
import { API } from '../../services';
import {
    setToastComponent,
    showErrorToast,
} from '../../services/setup/ApiResponseHandler';
import { useAuthenticate } from '../../hooks';
import greenSuccessCheck from '../../assets/images/green_success_icon.png';
import AIEduToast from '../../components/common/Toast/AIEduToast';
import { COUNTRY_LIST } from '../../utils/CountryList';
import { trimObjectValues } from '../../utils/Utils';
import { Checkbox } from 'primereact/checkbox';
import CreateOrganizationErrorModel from './CreateOrganizationErrorModel';
import {
    CITY_MAX_LENGTH,
    ORG_ADDRESS_MAX_LENGTH,
    ORG_NAME_MAX_LENGTH,
    PENDING,
    REDIRECT_DURATION,
    REJECTED,
    STATE_MAX_LENGTH,
    ZIP_CODE_MAX_LENGTH,
} from '../../utils/Constants';

const CreateOrganization = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);
    const { auth, ...rest } = useAuthenticate();
    const { accountInfo } = rest ?? {};
    const { account, organizations } = accountInfo ?? {};
    const { email, userStatus } = account ?? {};

    const [termInfo, setTermInfo] = useState({});
    const [policyInfo, setPolicyInfo] = useState({});
    const toastRef = useRef(null);
    const createBtnRef = useRef(null);

    const [visibleError, setVisibleError] = useState(false);
    const [navigationTimeout, setNavigationTimeout] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (
            userStatus !== PENDING &&
            userStatus !== REJECTED &&
            organizations &&
            organizations.length > 0
        ) {
            return;
        }

        API.getTermsAndPolicy('orgId').then(({ data }) => {
            const { terms } = data;
            setTermInfo(terms?.[0]);
            setPolicyInfo(terms?.[1]);
        });

        setToastComponent(toastRef.current);
    }, []);

    const initialFormState = {
        organizationName: '',
        address: '',
        city: '',
        country: '',
        state: '',
        zipCode: '',
    };

    const [isCheckedTerms, setIsCheckedTerms] = useState(false);

    const [formState, setFormState] = useReducer(
        CreateOrganizationFormReducer,
        initialFormState
    );

    const handleChange = (e) => {
        setFormState({
            type: 'SET_INPUT',
            field: e.target.name,
            payload: e.target.value,
        });
    };

    const disableCreateOrg = () => {
        return (
            !isCheckedTerms || formState.organizationName.trim().length === 0
        );
    };

    const CreateOrgModal = () => {
        const [counter, setCounter] = useState(10);
        useEffect(() => {
            let intervalId = null;
            if (navigationTimeout) {
                intervalId = setInterval(() => {
                    setCounter((prevCount) => prevCount - 1);
                    if (counter < 0) {
                        clearInterval(intervalId);
                    }
                }, 1000);
            }

            return () => clearInterval(intervalId);
        }, [navigationTimeout]);

        return (
            <AIEduDialog
                visible={visible}
                draggable={false}
                style={{ width: '40vw', minWidth: '40rem' }}
                onHide={() => {
                    if (!visible) return;
                    setVisible(false);
                }}
                content={
                    <>
                        <div
                            className='flex justify-content-center mt-2 mb-5'
                            style={{ height: '6rem' }}
                        >
                            <img
                                src={greenSuccessCheck}
                                style={{ height: '100%' }}
                            />
                        </div>

                        <div
                            className='flex justify-content-center mb-3'
                            style={{
                                fontFamily: 'SamsungOne-800',
                                fontSize: '24px',
                            }}
                        >
                            {f({ id: 'AI_EDU_ORG_ADDED_TITLE' })}
                        </div>

                        <div
                            className='flex justify-content-center'
                            style={{
                                fontFamily: 'SamsungOne-600',
                                fontSize: '16px',
                            }}
                        >
                            {f({ id: 'AI_EDU_ORG_ADDED_SUBTITLE' })}
                        </div>

                        <div className='flex justify-content-center mt-6 mb-4'>
                            <AIEduButton
                                label={f({ id: 'AI_EDU_VIEW_DASHBOARD' })}
                                className='btnSecondary'
                                style={{ width: '15rem' }}
                                onClick={() => {
                                    clearTimeout(navigationTimeout);
                                    navigate(DASHBOARD_PATH);
                                    setVisible(false);
                                }}
                            />
                        </div>
                        <div
                            className='flex justify-content-center my-3'
                            style={{ fontFamily: 'SamsungOne-500' }}
                        >
                            Redirecting in {counter}
                        </div>
                    </>
                }
            />
        );
    };

    const handleRedirect = () => {
        const timeOut = setTimeout(() => {
            navigate(DASHBOARD_PATH);
            setVisible(false);
        }, REDIRECT_DURATION);

        setNavigationTimeout(timeOut);
    };

    const createUserOrganization = (e) => {
        e.preventDefault();
        createBtnRef.current.blur();

        const payload = trimObjectValues(formState);
        if (payload.organizationName.length < 6) {
            return showErrorToast(
                'Organization name should be at least 6 characters long.'
            );
        }

        API.createOrganization(payload).then((res) => {
            const { data } = res ?? {};
            const { error } = data ?? {};
            if (res.status !== 201) {
                setVisibleError(true);
                setErrorMessage(error);
            } else {
                API.acceptTermsAndPolicy(data.organizationId, {
                    termId: termInfo?.termId,
                    policyId: policyInfo?.termId,
                }).then((response) => {
                    if (response?.status === 200) {
                        handleRedirect();
                        setVisible(true);
                        setErrorMessage('');
                    } else {
                        showErrorToast('Unable to accept terms and policy');
                    }
                });
            }
        });
    };

    const handleNavigation = (url) => {
        if (!url) return;
        window.open(url, '_blank');
    };

    if (
        userStatus !== PENDING &&
        userStatus !== REJECTED &&
        organizations &&
        organizations.length > 0
    ) {
        return <Navigate to={DASHBOARD_PATH} />;
    }

    return (
        <div className='page-layout'>
            <AIEduToast ref={toastRef} />
            <Header isLoginPage={true} />
            <div className='create-org'>
                <main className='main'>
                    <form>
                        <div className='create-org-box'>
                            <div className='create-org-header'>
                                <div>{f({ id: 'AI_EDU_CREATE_ORG' })}</div>
                                <div className='create-org-header-color'>
                                    {f({ id: 'AI_EDU_ADD_ORG_DETAILS' })}
                                </div>
                            </div>
                            <div className='email-address'>
                                <span>
                                    {f({ id: 'AI_EDU_EMAIL_ADDRESS' })}:{' '}
                                </span>
                                <span
                                    style={{
                                        paddingLeft: '5px',
                                        fontFamily: 'SamsungOne-500',
                                    }}
                                >
                                    {email}
                                </span>
                                <span className='create-org-header-color'>
                                    {f({
                                        id: 'AI_EDU_EMAIL_CANNOT_BE_CHANGED',
                                    })}
                                </span>
                            </div>

                            <div className='organization-name'>
                                <AIEduInputText
                                    name='organizationName'
                                    title={f({ id: 'AI_EDU_ORG_NAME' })}
                                    suffix={
                                        <span style={{ color: 'red' }}>*</span>
                                    }
                                    value={formState.organizationName}
                                    placeholder='Enter Organization Name'
                                    onChange={handleChange}
                                    maxLength={ORG_NAME_MAX_LENGTH}
                                />
                            </div>

                            <div className='organization-address'>
                                <div className='organization-address-header'>
                                    <div>{f({ id: 'AI_EDU_ORG_ADDRESS' })}</div>
                                </div>
                            </div>

                            <div className='organization-details'>
                                <div className='org_details_row1'>
                                    <div className='org_address'>
                                        <AIEduInputText
                                            name='address'
                                            title={f({ id: 'AI_EDU_ADDRESS' })}
                                            placeholder='Enter Address'
                                            value={formState.address}
                                            onChange={handleChange}
                                            maxLength={ORG_ADDRESS_MAX_LENGTH}
                                        />
                                    </div>
                                    <div className='org_city'>
                                        <AIEduInputText
                                            name='city'
                                            title={f({ id: 'AI_EDU_CITY' })}
                                            placeholder='Enter City'
                                            value={formState.city}
                                            onChange={handleChange}
                                            maxLength={CITY_MAX_LENGTH}
                                        />
                                    </div>
                                </div>
                                <div className='org_details_row2 mt-4'>
                                    <div
                                        className='org_country flex flex-column'
                                        style={{ gap: '2px' }}
                                    >
                                        <label>
                                            {f({ id: 'AI_EDU_COUNTRY' })}
                                        </label>
                                        <AIEduDropDown
                                            name='country'
                                            placeholder='Select Country'
                                            value={formState.country}
                                            optionLabel='name'
                                            options={COUNTRY_LIST}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div
                                        className='org_state'
                                        style={{ marginTop: '-1rem' }}
                                    >
                                        <AIEduInputText
                                            name='state'
                                            title={f({
                                                id: 'AI_EDU_STATE_PROVINCE',
                                            })}
                                            placeholder='Enter State/ Province'
                                            value={formState.state}
                                            onChange={handleChange}
                                            maxLength={STATE_MAX_LENGTH}
                                        />
                                    </div>
                                </div>
                                <div className='org_details_row3'>
                                    <div className='org_zip'>
                                        <AIEduInputNumber
                                            name='zipCode'
                                            title={f({ id: 'AI_EDU_ZIP_CODE' })}
                                            placeholder='Enter Code'
                                            value={formState.zipCode}
                                            onValueChange={handleChange}
                                            useGrouping={false}
                                            maxLength={ZIP_CODE_MAX_LENGTH}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className='organization-terms flex mt-4'>
                                <Checkbox
                                    checked={isCheckedTerms}
                                    name='termsNCondition'
                                    className='mr-4'
                                    onChange={() =>
                                        setIsCheckedTerms(!isCheckedTerms)
                                    }
                                />
                                <div>
                                    I agree to the{' '}
                                    <a
                                        href={termInfo?.termUrl}
                                        target='_blank'
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Terms and Conditions
                                    </a>{' '}
                                    and the{' '}
                                    <a
                                        href={policyInfo?.termUrl}
                                        target='_blank'
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Privacy Policy
                                    </a>
                                    .{' '}
                                </div>
                            </div>

                            <AIEduButton
                                className='create-org-btn btnPrimary'
                                label={f({ id: 'AI_EDU_CREATE_ORG' })}
                                ref={createBtnRef}
                                onClick={createUserOrganization}
                                disabled={disableCreateOrg()}
                            />

                            <CreateOrgModal />
                            <CreateOrganizationErrorModel
                                visible={visibleError}
                                setVisible={setVisibleError}
                                errorMessage={errorMessage}
                            />
                        </div>
                    </form>
                </main>
                <footer className='footer'>
                    <div
                        onClick={() => handleNavigation(termInfo?.termUrl)}
                        className='cursor-pointer hover:underline'
                    >
                        Terms and Condition
                    </div>
                    <div
                        onClick={() => handleNavigation(policyInfo?.termUrl)}
                        className='cursor-pointer hover:underline'
                    >
                        Privacy Policy
                    </div>
                    <div>
                        Copyright &copy; Samsung Electronics Co., Ltd. All
                        Rights Reserved.
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default CreateOrganization;
